import { useState, useCallback, useEffect } from "react";
import type { NewsItem } from "@/types/news.types";
import { getNewsList } from "@/api/news.api";

const PAGE_SIZE = 12;

export interface UseNewsResult {
  news: NewsItem[];
  total: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
}

export function useNews(): UseNewsResult {
  const [rows, setRows] = useState<NewsItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchPage = useCallback(async (pageNum: number, append: boolean) => {
    try {
      const { rows: newRows, total: t } = await getNewsList({ page: pageNum, pageSize: PAGE_SIZE });
      setTotal(t);
      if (append) {
        setRows((prev) => {
          const byId = new Map(prev.map((n) => [n.id, n]));
          newRows.forEach((r) => byId.set(r.id, r));
          return Array.from(byId.values());
        });
      } else {
        setRows(newRows);
      }
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
      if (!append) setRows([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPage(1);
    setIsLoading(true);
    setError(null);
    setRows([]);
    getNewsList({ page: 1, pageSize: PAGE_SIZE })
      .then(({ rows: r, total: t }) => {
        if (!cancelled) {
          setRows(r);
          setTotal(t);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || rows.length >= total) return;
    setIsLoadingMore(true);
    const next = page + 1;
    await fetchPage(next, true);
    setPage(next);
    setIsLoadingMore(false);
  }, [fetchPage, isLoadingMore, page, rows.length, total]);

  const refetch = useCallback(async () => {
    setPage(1);
    setIsLoading(true);
    setError(null);
    await fetchPage(1, false);
    setIsLoading(false);
  }, [fetchPage]);

  return {
    news: rows,
    total,
    isLoading,
    isLoadingMore,
    error,
    hasMore: total > rows.length,
    loadMore,
    refetch,
  };
}
